'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import _ from 'lodash'
import { useRouter } from 'next/navigation'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { authApi } from '@/api'
import { Alert, Button, Input, Link, Password } from '@/components'
import { pages } from '@/settings'
import { useGlobalStore } from '@/store/provider'
import type { Auth } from '@/types'

const schema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email format'),
  password: z.string().min(1, 'Password is required'),
})

export default function SignIn() {
  const { user, setUser } = useGlobalStore((state) => state)
  const router = useRouter()

  useEffect(() => {
    if (!_.isEmpty(user)) {
      router.push('/')
    }
  }, [user, router])

  // react-hook-form
  type SignInData = z.infer<typeof schema>
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInData>({
    resolver: zodResolver(schema),
  })

  const signIn = async (data: Auth.SignInRequest) => {
    const result = await authApi.signIn(data)
    if (result?.challenge_name === 'NEW_PASSWORD_REQUIRED') {
      router.push(`${pages.newPassword.href}?email=${data.email}&session=${result.session}`)
    } else {
      setUser(result, null)
    }
  }
  const { mutate, error, isPending } = useMutation({ mutationFn: signIn })
  const onSubmit = (data: SignInData) => {
    mutate(data)
  }

  return (
    <>
      <h2 className='mt-10 text-center text-3xl/10 font-bold tracking-tight'>Sign in to Cruz Admin</h2>
      <div className='w-full text-center text-zinc-700 dark:text-zinc-400 py-2 mb-6'>Please sign in with your AWS Cognito credentials</div>
      <form noValidate autoComplete='off' className='space-y-3' onSubmit={handleSubmit(onSubmit)}>
        <Input
          label='Email'
          className='text-base py-2'
          placeholder='email'
          type='email'
          {...register('email')}
          data-error={_.get(errors, 'email.message')}
        />
        <Password label='Password' className='text-base py-2' {...register('password')} data-error={_.get(errors, 'password.message')} />
        {error && <Alert variant='error' message={error.message} />}
        <div className='pt-4'>
          <Button type='submit' className='w-full h-10' working={isPending}>
            Sign in
          </Button>
        </div>
        <div className='pt-4 text-center'>
          <Link href={pages.forgotPassword.href}>Forgot Password?</Link>
        </div>
      </form>
    </>
  )
}
